import React, { useEffect, useRef, useState } from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';

import { useDrawHistory } from '../hooks/useDrawHistory';

import PlaceholderSignature from './PlaceholderSignature';

const DrawSignature = ({
  active,
  value,
  onChange,
  onHistoryChange,
  penColor = '#000000',
  canvasHeight = 200,
  containerWidth = 0,
  handleRef,
  'data-qa': dataQa
}) => {
  /** @type {React.MutableRefObject<any>} */
  const sigPadRef = useRef(null);
  /** @type {React.MutableRefObject<string | null>} */
  const syncedValueRef = useRef(null);
  const history = useDrawHistory(sigPadRef);
  const { setBaseline, reset, undo, redo, capture, canUndo, canRedo } = history;
  const [hasContent, setHasContent] = useState(false);

  useEffect(() => {
    onHistoryChange?.({ canUndo, canRedo });
  }, [canUndo, canRedo, onHistoryChange]);

  useEffect(() => {
    if (!sigPadRef.current || !containerWidth) return;

    const canvas = sigPadRef.current.getCanvas();
    const opts = { ratio: 1, width: containerWidth, height: canvasHeight };
    const needsResize = canvas.width !== containerWidth || canvas.height !== canvasHeight;

    if (needsResize) {
      const hadContent = !sigPadRef.current.isEmpty() || !!syncedValueRef.current;
      const snapshot = hadContent ? canvas.toDataURL('image/png') : null;
      canvas.width = containerWidth;
      canvas.height = canvasHeight;
      if (snapshot) sigPadRef.current.fromDataURL(snapshot, opts);
    }

    if (!active) return;

    if (value === undefined) return;

    if (value !== syncedValueRef.current) {
      if (value) {
        sigPadRef.current.fromDataURL(value, opts);
        setBaseline(value);
        setHasContent(true);
      } else if (syncedValueRef.current) {
        sigPadRef.current.clear();
        reset();
        setHasContent(false);
      }
      syncedValueRef.current = value || null;
    }
  }, [active, value, containerWidth, canvasHeight, setBaseline, reset]);

  const getCurrentPng = () =>
    sigPadRef.current && !sigPadRef.current.isEmpty()
      ? sigPadRef.current.getCanvas().toDataURL('image/png')
      : null;

  useEffect(() => {
    if (!handleRef) return undefined;
    handleRef.current = {
      getValue: () => getCurrentPng(),
      clear: () => {
        if (sigPadRef.current) sigPadRef.current.clear();
        reset();
        syncedValueRef.current = null;
        setHasContent(false);
      },
      undo: () => {
        const result = undo();
        syncedValueRef.current = result;
        setHasContent(!!result);
        return result;
      },
      redo: () => {
        const result = redo();
        syncedValueRef.current = result;
        setHasContent(!!result);
        return result;
      }
    };
    return () => {
      if (handleRef) handleRef.current = null;
    };
  }, [handleRef, reset, undo, redo]);

  const handleEnd = () => {
    capture();
    const next = getCurrentPng();
    syncedValueRef.current = next;
    setHasContent(!!next);
    if (active) onChange?.(next);
  };

  return (
    <div
      className="relative min-w-0 overflow-hidden rounded-md border border-dashed border-neutral-500 bg-primary-10"
      style={{ height: canvasHeight }}>
      {containerWidth > 0 && (
        <ReactSignatureCanvas
          ref={sigPadRef}
          penColor={penColor}
          minWidth={0.8}
          maxWidth={2.2}
          velocityFilterWeight={0.6}
          throttle={8}
          minDistance={1}
          dotSize={1.2}
          onBegin={() => setHasContent(true)}
          onEnd={handleEnd}
          canvasProps={{
            width: containerWidth,
            height: canvasHeight,
            'data-qa': active ? dataQa : undefined,
            style: {
              width: '100%',
              height: canvasHeight,
              maxWidth: '100%',
              background: 'transparent',
              border: 'none',
              borderRadius: 0,
              display: 'block'
            }
          }}
        />
      )}
      {!hasContent && <PlaceholderSignature canvasHeight={canvasHeight} />}
    </div>
  );
};

export default DrawSignature;
